import React, { useState, useEffect, useRef } from 'react';
import { FiPlus, FiEdit, FiTrash2, FiX, FiUpload, FiToggleLeft, FiToggleRight, FiChevronDown, FiChevronRight } from 'react-icons/fi';
import axios from 'axios';

const VendorServicesTab = ({ vendorId, vendorData, onDataChange }) => {
  const API_BASE_URL = `${process.env.REACT_APP_API_URL}/api`;
  const token = localStorage.getItem('token');
  const authHeader = { Authorization: `Bearer ${token}` };

  // Data
  const [services, setServices] = useState([]);
  const [packages, setPackages] = useState([]);
  const [addons, setAddons] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingPackages, setLoadingPackages] = useState(false);
  const [loadingAddons, setLoadingAddons] = useState(false);

  // Service Modal
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [serviceForm, setServiceForm] = useState({
    serviceId: '', name: '', price: '', priceType: 'fixed', duration: '', description: '', gender: '',
    parentServiceId: '', parentServiceName: ''
  });
  const [serviceImageFile, setServiceImageFile] = useState(null);
  const [savingService, setSavingService] = useState(false);

  // Package Modal
  const [showPackageModal, setShowPackageModal] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [packageForm, setPackageForm] = useState({
    name: '', description: '', price: '', duration: '', services: []
  });
  const [savingPackage, setSavingPackage] = useState(false);

  // Addon Modal
  const [showAddonModal, setShowAddonModal] = useState(false);
  const [editingAddon, setEditingAddon] = useState(null);
  const [addonForm, setAddonForm] = useState({
    name: '', description: '', category: 'other', price: '', tags: '',
    displayOrder: 0, eligibleForSubsidy: true
  });
  const [addonImageFile, setAddonImageFile] = useState(null);
  const [savingAddon, setSavingAddon] = useState(false);

  // Selection mode for services
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedServiceIds, setSelectedServiceIds] = useState(new Set());
  const [deletingSelected, setDeletingSelected] = useState(false);

  // Active sub-tab
  const [activeSection, setActiveSection] = useState('services');
  // Expanded parent services in tree view
  const [expandedServices, setExpandedServices] = useState(new Set());

  const mountedRef = useRef(true);
  useEffect(() => {
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    let cancelled = false;

    const loadData = async () => {
      if (!vendorId) return;

      setLoadingServices(true);
      setLoadingPackages(true);
      setLoadingAddons(true);

      try {
        const [servicesRes, packagesRes, addonsRes, categoriesRes] = await Promise.allSettled([
          axios.get(`${API_BASE_URL}/admin/vendor-service/${vendorId}`, { headers: authHeader }),
          axios.get(`${API_BASE_URL}/admin/vendor-package/vendor/${vendorId}`, { headers: authHeader }),
          axios.get(`${API_BASE_URL}/admin/vendor-addon/${vendorId}?includeInactive=true`, { headers: authHeader }),
          axios.get(`${API_BASE_URL}/admin/service-categories`, { headers: authHeader })
        ]);

        if (cancelled) return;

        if (servicesRes.status === 'fulfilled' && servicesRes.value.data.success)
          setServices(servicesRes.value.data.data || []);
        if (packagesRes.status === 'fulfilled' && packagesRes.value.data.success)
          setPackages(packagesRes.value.data.data || []);
        if (addonsRes.status === 'fulfilled' && addonsRes.value.data.success)
          setAddons(addonsRes.value.data.data || []);
        if (categoriesRes.status === 'fulfilled' && categoriesRes.value.data.success)
          setServiceCategories(categoriesRes.value.data.data || []);
      } catch (err) {
        console.error('Error loading data:', err);
      } finally {
        if (!cancelled) {
          setLoadingServices(false);
          setLoadingPackages(false);
          setLoadingAddons(false);
        }
      }
    };

    loadData();

    return () => { cancelled = true; };
  }, [vendorId]);

  // ========== FETCH FUNCTIONS ==========

  const fetchServices = async () => {
    if (!mountedRef.current) return;
    setLoadingServices(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/vendor-service/${vendorId}`, {
        headers: authHeader
      });
      if (mountedRef.current && res.data.success) setServices(res.data.data || []);
    } catch (err) {
      console.error('Error fetching services:', err);
    } finally {
      if (mountedRef.current) setLoadingServices(false);
    }
  };

  const fetchPackages = async () => {
    if (!mountedRef.current) return;
    setLoadingPackages(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/vendor-package/vendor/${vendorId}`, {
        headers: authHeader
      });
      if (mountedRef.current && res.data.success) setPackages(res.data.data || []);
    } catch (err) {
      console.error('Error fetching packages:', err);
    } finally {
      if (mountedRef.current) setLoadingPackages(false);
    }
  };

  const fetchAddons = async () => {
    if (!mountedRef.current) return;
    setLoadingAddons(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/admin/vendor-addon/${vendorId}?includeInactive=true`, {
        headers: authHeader
      });
      if (mountedRef.current && res.data.success) setAddons(res.data.data || []);
    } catch (err) {
      console.error('Error fetching addons:', err);
    } finally {
      if (mountedRef.current) setLoadingAddons(false);
    }
  };


  // ========== SERVICE CRUD ==========

  const openCreateService = () => {
    setEditingService(null);
    setServiceForm({ serviceId: '', name: '', price: '', priceType: 'fixed', duration: '', description: '', gender: '', parentServiceId: '', parentServiceName: '' });
    setServiceImageFile(null);
    setShowServiceModal(true);
  };

  const openCreateSubService = (parentService) => {
    setEditingService(null);
    setServiceForm({
      serviceId: '', name: '', price: '', priceType: 'fixed', duration: '', description: '', gender: '',
      parentServiceId: parentService._id,
      parentServiceName: parentService.name
    });
    setServiceImageFile(null);
    setShowServiceModal(true);
  };

  const openEditService = (service) => {
    setEditingService(service);
    setServiceForm({
      serviceId: service.serviceId?._id || service.serviceId || '',
      name: service.name || '',
      price: service.price || '',
      priceType: service.priceType || 'fixed',
      duration: service.duration || '',
      description: service.description || '',
      gender: service.gender || '',
      parentServiceId: service.parentServiceId?._id || service.parentServiceId || '',
      parentServiceName: service.parentServiceId?.name || ''
    });
    setServiceImageFile(null);
    setShowServiceModal(true);
  };

  const handleSaveService = async (e) => {
    e.preventDefault();
    setSavingService(true);
    const isSubService = !!serviceForm.parentServiceId && !editingService;
    try {
      const formData = new FormData();
      if (!editingService) formData.append('vendorId', vendorId);
      if (!isSubService && serviceForm.serviceId) formData.append('serviceId', serviceForm.serviceId);
      if (isSubService) formData.append('parentServiceId', serviceForm.parentServiceId);
      formData.append('name', serviceForm.name);
      if (serviceForm.price) formData.append('price', serviceForm.price);
      formData.append('priceType', serviceForm.priceType || 'fixed');
      if (serviceForm.duration) formData.append('duration', serviceForm.duration);
      if (serviceForm.description) formData.append('description', serviceForm.description);
      if (editingService) {
        formData.append('gender', serviceForm.gender || '');
      } else {
        if (serviceForm.gender) formData.append('gender', serviceForm.gender);
      }
      if (serviceImageFile) formData.append('image', serviceImageFile);

      if (editingService) {
        if (serviceForm.serviceId) formData.append('categoryId', serviceForm.serviceId);
        await axios.put(`${API_BASE_URL}/admin/vendor-service/${editingService._id}`, formData, {
          headers: { ...authHeader, 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_BASE_URL}/admin/vendor-service`, formData, {
          headers: { ...authHeader, 'Content-Type': 'multipart/form-data' }
        });
      }

      if (!mountedRef.current) return;
      setShowServiceModal(false);
      fetchServices();
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error saving service:', err);
      if (mountedRef.current) alert(err.response?.data?.message || 'Failed to save service');
    } finally {
      if (mountedRef.current) setSavingService(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/admin/vendor-service/${serviceId}`, {
        headers: authHeader
      });
      if (res.data.success && mountedRef.current) {
        fetchServices();
        fetchPackages();
        if (onDataChange) onDataChange();
      }
    } catch (err) {
      console.error('Error deleting service:', err);
      if (mountedRef.current) {
        alert(err.response?.data?.message || 'Failed to delete service');
      }
    }
  };

  const toggleSelectService = (id) => {
    setSelectedServiceIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedServiceIds.size === services.length) {
      setSelectedServiceIds(new Set());
    } else {
      setSelectedServiceIds(new Set(services.map(s => s._id)));
    }
  };

  const exitSelectMode = () => {
    setIsSelectMode(false);
    setSelectedServiceIds(new Set());
  };

  const handleDeleteSelected = async () => {
    if (selectedServiceIds.size === 0) return;
    if (!window.confirm(`Delete ${selectedServiceIds.size} selected service(s)? This will also remove their sub-services.`)) return;
    setDeletingSelected(true);
    try {
      const res = await axios.delete(`${API_BASE_URL}/admin/vendor-services/bulk`, {
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        data: { serviceIds: Array.from(selectedServiceIds) }
      });
      if (res.data.success && mountedRef.current) {
        exitSelectMode();
        fetchServices();
        if (onDataChange) onDataChange();
      }
    } catch (err) {
      console.error('Error bulk deleting services:', err);
      if (mountedRef.current) {
        const msg = err.response?.data?.message || 'Failed to delete selected services';
        if (err.response?.data?.packages) {
          alert(`${msg}\nUsed in: ${err.response.data.packages.map(p => p.name).join(', ')}`);
        } else {
          alert(msg);
        }
      }
    } finally {
      if (mountedRef.current) setDeletingSelected(false);
    }
  };

  // ========== PACKAGE CRUD ==========

  const openCreatePackage = () => {
    setEditingPackage(null);
    setPackageForm({ name: '', description: '', price: '', duration: '', services: [] });
    setShowPackageModal(true);
  };

  const openEditPackage = (pkg) => {
    setEditingPackage(pkg);
    const serviceIds = (pkg.services || []).map(s => s.serviceId || s._id || s);
    setPackageForm({
      name: pkg.name || '',
      description: pkg.description || '',
      price: pkg.price || '',
      duration: pkg.duration || '',
      services: serviceIds
    });
    setShowPackageModal(true);
  };

  const handleSavePackage = async (e) => {
    e.preventDefault();
    setSavingPackage(true);
    try {
      const body = {
        name: packageForm.name,
        description: packageForm.description,
        price: packageForm.price,
        duration: packageForm.duration,
        services: packageForm.services
      };

      if (editingPackage) {
        await axios.put(`${API_BASE_URL}/admin/vendor-package/${editingPackage._id}`, body, {
          headers: { ...authHeader, 'Content-Type': 'application/json' }
        });
      } else {
        await axios.post(`${API_BASE_URL}/admin/vendor-package`, { ...body, vendorId }, {
          headers: { ...authHeader, 'Content-Type': 'application/json' }
        });
      }

      if (!mountedRef.current) return;
      setShowPackageModal(false);
      fetchPackages();
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error saving package:', err);
      if (mountedRef.current) alert(err.response?.data?.message || 'Failed to save package');
    } finally {
      if (mountedRef.current) setSavingPackage(false);
    }
  };

  const handleDeletePackage = async (packageId) => {
    if (!window.confirm('Are you sure you want to delete this package?')) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/admin/vendor-package/${packageId}`, {
        headers: authHeader
      });
      if (res.data.success && mountedRef.current) {
        fetchPackages();
        if (onDataChange) onDataChange();
      }
    } catch (err) {
      console.error('Error deleting package:', err);
      if (mountedRef.current) alert(err.response?.data?.message || 'Failed to delete package');
    }
  };

  // ========== ADDON CRUD ==========

  const openCreateAddon = () => {
    setEditingAddon(null);
    setAddonForm({
      name: '', description: '', category: 'other', price: '', tags: '',
      displayOrder: 0, eligibleForSubsidy: true
    });
    setAddonImageFile(null);
    setShowAddonModal(true);
  };

  const openEditAddon = (addon) => {
    setEditingAddon(addon);
    setAddonForm({
      name: addon.name || '',
      description: addon.description || '',
      category: addon.category || 'other',
      price: addon.price || '',
      tags: (addon.tags || []).join(', '),
      displayOrder: addon.displayOrder || 0,
      eligibleForSubsidy: addon.eligibleForSubsidy ?? true
    });
    setAddonImageFile(null);
    setShowAddonModal(true);
  };

  const handleSaveAddon = async (e) => {
    e.preventDefault();
    setSavingAddon(true);
    try {
      const formData = new FormData();
      if (!editingAddon) formData.append('vendorId', vendorId);
      formData.append('name', addonForm.name);
      formData.append('description', addonForm.description);
      formData.append('category', addonForm.category);
      formData.append('price', addonForm.price);
      formData.append('displayOrder', addonForm.displayOrder);
      formData.append('eligibleForSubsidy', addonForm.eligibleForSubsidy);
      if (addonForm.tags) {
        const tagsArray = addonForm.tags.split(',').map(t => t.trim()).filter(Boolean);
        formData.append('tags', JSON.stringify(tagsArray));
      }
      if (addonImageFile) formData.append('image', addonImageFile);

      if (editingAddon) {
        await axios.put(`${API_BASE_URL}/admin/vendor-addon/${editingAddon._id}`, formData, {
          headers: { ...authHeader, 'Content-Type': 'multipart/form-data' }
        });
      } else {
        await axios.post(`${API_BASE_URL}/admin/vendor-addon`, formData, {
          headers: { ...authHeader, 'Content-Type': 'multipart/form-data' }
        });
      }

      if (!mountedRef.current) return;
      setShowAddonModal(false);
      fetchAddons();
      if (onDataChange) onDataChange();
    } catch (err) {
      console.error('Error saving addon:', err);
      if (mountedRef.current) alert(err.response?.data?.message || 'Failed to save add-on');
    } finally {
      if (mountedRef.current) setSavingAddon(false);
    }
  };

  const handleToggleAddon = async (addonId) => {
    try {
      await axios.patch(`${API_BASE_URL}/admin/vendor-addon/${addonId}/toggle`, {}, {
        headers: authHeader
      });
      if (mountedRef.current) fetchAddons();
    } catch (err) {
      console.error('Error toggling addon:', err);
      if (mountedRef.current) alert(err.response?.data?.message || 'Failed to toggle add-on');
    }
  };

  const handleDeleteAddon = async (addonId) => {
    if (!window.confirm('Are you sure you want to delete this add-on?')) return;
    try {
      const res = await axios.delete(`${API_BASE_URL}/admin/vendor-addon/${addonId}`, {
        headers: authHeader
      });
      if (res.data.success && mountedRef.current) {
        fetchAddons();
        if (onDataChange) onDataChange();
      }
    } catch (err) {
      console.error('Error deleting addon:', err);
      if (mountedRef.current) alert(err.response?.data?.message || 'Failed to delete add-on');
    }
  };

  const togglePackageService = (svcId) => {
    setPackageForm(prev => ({
      ...prev,
      services: prev.services.includes(svcId)
        ? prev.services.filter(id => id !== svcId)
        : [...prev.services, svcId]
    }));
  };

  const addonCategories = [
    { value: 'nail_care', label: 'Nail Care' },
    { value: 'hair_care', label: 'Hair Care' },
    { value: 'skin_care', label: 'Skin Care' },
    { value: 'touch_up', label: 'Touch Up' },
    { value: 'accessories', label: 'Accessories' },
    { value: 'other', label: 'Other' }
  ];

  // ========== RENDER ==========

  return (
    <div>
      {/* Sub-tab Navigation */}
      <div className="flex gap-2 mb-6 border-b pb-3">
        {['services', 'packages', 'addons'].map(tab => (
          <button
            key={tab}
            onClick={() => setActiveSection(tab)}
            className={`px-4 py-2 rounded-t-lg font-medium text-sm capitalize ${
              activeSection === tab
                ? 'bg-blue-500 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {tab === 'addons' ? 'Add-ons' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            <span className="ml-2 text-xs bg-white bg-opacity-30 px-1.5 py-0.5 rounded-full">
              {tab === 'services' ? services.length : tab === 'packages' ? packages.length : addons.length}
            </span>
          </button>
        ))}
      </div>

      {/* ===== SERVICES SECTION ===== */}
      {activeSection === 'services' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <div className="flex items-center gap-3">
              <h3 className="text-xl font-bold text-gray-800">Services</h3>
              {isSelectMode && (
                <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={services.length > 0 && selectedServiceIds.size === services.length}
                    onChange={toggleSelectAll}
                    className="w-4 h-4 text-blue-600 cursor-pointer"
                  />
                  Select All
                </label>
              )}
            </div>
            <div className="flex items-center gap-2">
              {isSelectMode ? (
                <>
                  {selectedServiceIds.size > 0 && (
                    <button
                      onClick={handleDeleteSelected}
                      disabled={deletingSelected}
                      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm font-medium disabled:opacity-50"
                    >
                      <FiTrash2 /> {deletingSelected ? 'Deleting...' : `Delete (${selectedServiceIds.size})`}
                    </button>
                  )}
                  <button
                    onClick={exitSelectMode}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                  >
                    <FiX /> Cancel
                  </button>
                </>
              ) : (
                <>
                  {services.length > 0 && (
                    <button
                      onClick={() => setIsSelectMode(true)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 text-sm font-medium"
                    >
                      Select
                    </button>
                  )}
                  <button
                    onClick={openCreateService}
                    className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
                  >
                    <FiPlus /> Add Service
                  </button>
                </>
              )}
            </div>
          </div>

          {loadingServices ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : services.length > 0 ? (() => {
            // Build tree: group children under their parent
            const childrenMap = {};
            const rootServices = [];
            services.forEach(s => {
              const parentId = typeof s.parentServiceId === 'object' ? s.parentServiceId?._id : s.parentServiceId;
              if (parentId) {
                if (!childrenMap[parentId]) childrenMap[parentId] = [];
                childrenMap[parentId].push(s);
              } else {
                rootServices.push(s);
              }
            });

            const toggleExpand = (id) => {
              setExpandedServices(prev => {
                const next = new Set(prev);
                next.has(id) ? next.delete(id) : next.add(id);
                return next;
              });
            };

            const renderServiceRow = (service, isChild = false) => {
              const children = childrenMap[service._id] || [];
              const hasChildren = children.length > 0 || service.hasChildren;
              const isExpanded = expandedServices.has(service._id);

              return (
                <React.Fragment key={service._id}>
                  <div
                    className={`flex items-center gap-3 px-4 py-3 border-b border-gray-100 hover:bg-gray-50 transition-colors ${
                      selectedServiceIds.has(service._id) ? 'bg-blue-50' : ''
                    } ${isChild ? 'pl-12 bg-gray-50/50' : ''}`}
                    onClick={isSelectMode ? () => toggleSelectService(service._id) : undefined}
                    style={isSelectMode ? { cursor: 'pointer' } : undefined}
                  >
                    {/* Select checkbox */}
                    {isSelectMode && (
                      <input
                        type="checkbox"
                        checked={selectedServiceIds.has(service._id)}
                        onChange={() => toggleSelectService(service._id)}
                        onClick={e => e.stopPropagation()}
                        className="w-4 h-4 text-blue-600 cursor-pointer flex-shrink-0"
                      />
                    )}

                    {/* Expand/collapse toggle for parents */}
                    {!isChild && hasChildren ? (
                      <button
                        onClick={(e) => { e.stopPropagation(); toggleExpand(service._id); }}
                        className="p-1 hover:bg-gray-200 rounded flex-shrink-0"
                      >
                        {isExpanded
                          ? <FiChevronDown className="w-4 h-4 text-gray-500" />
                          : <FiChevronRight className="w-4 h-4 text-gray-500" />
                        }
                      </button>
                    ) : !isChild ? (
                      <span className="w-6 flex-shrink-0" />
                    ) : null}

                    {/* Service info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${isChild ? 'text-gray-700' : 'text-gray-900'}`}>
                          {service.name}
                        </span>
                        {service.serviceId?.name && !isChild && (
                          <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded">
                            {service.serviceId.name}
                          </span>
                        )}
                        {hasChildren && !isChild && (
                          <span className="text-xs text-gray-400">
                            ({children.length} sub-service{children.length !== 1 ? 's' : ''})
                          </span>
                        )}
                      </div>
                      {service.description && (
                        <p className="text-xs text-gray-500 truncate mt-0.5">{service.description}</p>
                      )}
                    </div>

                    {/* Price */}
                    <div className="flex-shrink-0 text-right min-w-[80px]">
                      {service.price != null ? (
                        <span className="font-bold text-sm text-blue-600">
                          ₹{service.price}
                          {service.priceType === 'onwards' && (
                            <span className="text-xs font-normal text-gray-400 ml-0.5">onwards</span>
                          )}
                        </span>
                      ) : (
                        <span className="text-xs text-gray-400 italic">—</span>
                      )}
                    </div>

                    {/* Actions */}
                    {!isSelectMode && (
                      <div className="flex gap-1 flex-shrink-0">
                        {!isChild && (
                          <button
                            onClick={() => openCreateSubService(service)}
                            className="p-1.5 hover:bg-purple-50 rounded"
                            title="Add Sub-service"
                          >
                            <FiPlus className="w-3.5 h-3.5 text-purple-600" />
                          </button>
                        )}
                        <button
                          onClick={() => openEditService(service)}
                          className="p-1.5 hover:bg-yellow-50 rounded"
                          title="Edit"
                        >
                          <FiEdit className="w-3.5 h-3.5 text-yellow-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteService(service._id)}
                          className="p-1.5 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <FiTrash2 className="w-3.5 h-3.5 text-red-600" />
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Render children when expanded */}
                  {!isChild && isExpanded && children.map(child => renderServiceRow(child, true))}
                </React.Fragment>
              );
            };

            return (
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                {/* Table header */}
                <div className="flex items-center gap-3 px-4 py-2.5 bg-gray-50 border-b border-gray-200 text-xs font-medium text-gray-500 uppercase tracking-wide">
                  {isSelectMode && <span className="w-4 flex-shrink-0" />}
                  <span className="w-6 flex-shrink-0" />
                  <span className="flex-1">Service</span>
                  <span className="min-w-[80px] text-right">Price</span>
                  {!isSelectMode && <span className="w-[100px] text-right">Actions</span>}
                </div>
                {rootServices.map(service => renderServiceRow(service))}
              </div>
            );
          })() : (
            <p className="text-gray-500 italic py-8 text-center">No services added yet</p>
          )}
        </div>
      )}

      {/* ===== PACKAGES SECTION ===== */}
      {activeSection === 'packages' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-800">Packages</h3>
            <button
              onClick={openCreatePackage}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
            >
              <FiPlus /> Add Package
            </button>
          </div>

          {loadingPackages ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : packages.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {packages.map(pkg => (
                <div key={pkg._id} className="bg-gray-50 rounded-lg shadow-md p-5 border border-gray-200">
                  <div className="flex justify-between items-start mb-3">
                    <h4 className="font-bold text-lg">{pkg.name}</h4>
                    <div className="flex items-center gap-2">
                      <span className="font-bold text-lg text-blue-600">₹{pkg.price}</span>
                      <button
                        onClick={() => openEditPackage(pkg)}
                        className="p-1.5 hover:bg-yellow-50 rounded"
                        title="Edit"
                      >
                        <FiEdit className="w-4 h-4 text-yellow-600" />
                      </button>
                      <button
                        onClick={() => handleDeletePackage(pkg._id)}
                        className="p-1.5 hover:bg-red-50 rounded"
                        title="Delete"
                      >
                        <FiTrash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </div>
                  {pkg.description && <p className="text-sm text-gray-600 mb-4">{pkg.description}</p>}
                  <h5 className="font-medium text-sm mb-2 text-gray-700">Included Services:</h5>
                  <div className="space-y-2">
                    {pkg.services && pkg.services.map((service, idx) => (
                      <div key={idx} className="flex justify-between items-center bg-white p-2 rounded">
                        <span className="text-sm">{service.name}</span>
                        <span className="text-sm font-medium">₹{service.price}</span>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic py-8 text-center">No packages added yet</p>
          )}
        </div>
      )}

      {/* ===== ADDONS SECTION ===== */}
      {activeSection === 'addons' && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-800">Add-ons</h3>
            <button
              onClick={openCreateAddon}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 text-sm font-medium"
            >
              <FiPlus /> Add Add-on
            </button>
          </div>

          {loadingAddons ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          ) : addons.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {addons.map(addon => (
                <div key={addon._id} className={`bg-white rounded-3xl shadow-card overflow-hidden border ${addon.isAvailable !== false ? 'border-gray-200' : 'border-red-200 opacity-70'}`}>
                  {addon.image && (
                    <div className="relative h-32">
                      <img
                        src={addon.image}
                        alt={addon.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-4">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4 className="font-bold text-base">{addon.name}</h4>
                        {addon.category && addon.category !== 'other' && (
                          <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">
                            {addonCategories.find(c => c.value === addon.category)?.label || addon.category}
                          </span>
                        )}
                      </div>
                      <span className="font-bold text-blue-600">₹{addon.price}</span>
                    </div>
                    {addon.description && (
                      <p className="text-sm text-gray-600 mb-2 line-clamp-2">{addon.description}</p>
                    )}
                    {addon.tags && addon.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mb-2">
                        {addon.tags.map((tag, i) => (
                          <span key={i} className="text-xs bg-gray-100 text-gray-600 px-1.5 py-0.5 rounded">{tag}</span>
                        ))}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t">
                      <button
                        onClick={() => handleToggleAddon(addon._id)}
                        className={`flex items-center gap-1 text-xs font-medium px-2 py-1 rounded ${
                          addon.isAvailable !== false
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}
                      >
                        {addon.isAvailable !== false ? <FiToggleRight className="w-4 h-4" /> : <FiToggleLeft className="w-4 h-4" />}
                        {addon.isAvailable !== false ? 'Active' : 'Inactive'}
                      </button>
                      <div className="flex gap-1">
                        <button
                          onClick={() => openEditAddon(addon)}
                          className="p-1.5 hover:bg-yellow-50 rounded"
                          title="Edit"
                        >
                          <FiEdit className="w-4 h-4 text-yellow-600" />
                        </button>
                        <button
                          onClick={() => handleDeleteAddon(addon._id)}
                          className="p-1.5 hover:bg-red-50 rounded"
                          title="Delete"
                        >
                          <FiTrash2 className="w-4 h-4 text-red-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 italic py-8 text-center">No add-ons added yet</p>
          )}
        </div>
      )}

      {/* ===== SERVICE MODAL ===== */}
      {showServiceModal && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-5 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold">
                {editingService ? 'Edit Service' : serviceForm.parentServiceId ? 'Add Sub-service' : 'Add Service'}
              </h3>
              <button onClick={() => setShowServiceModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveService} className="p-5 space-y-4">
              {/* Sub-service context banner */}
              {serviceForm.parentServiceId && !editingService && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-3 text-sm text-purple-800">
                  <span className="font-medium">Sub-service of:</span> {serviceForm.parentServiceName}
                </div>
              )}

              {/* Service Category — hidden for sub-services */}
              {!serviceForm.parentServiceId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Service Category *</label>
                  <select
                    required={!serviceForm.parentServiceId}
                    value={serviceForm.serviceId}
                    onChange={(e) => setServiceForm({ ...serviceForm, serviceId: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="">Select Category</option>
                    {serviceCategories.map(cat => (
                      <option key={cat._id} value={cat._id}>{cat.name}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Service Name *</label>
                <input
                  type="text"
                  required
                  value={serviceForm.name}
                  onChange={(e) => setServiceForm({ ...serviceForm, name: e.target.value })}
                  placeholder="e.g., Haircut"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Price & Price Type */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Price (₹) <span className="text-gray-400 font-normal">(optional)</span>
                  </label>
                  <input
                    type="text"
                    inputMode="numeric"
                    value={serviceForm.price}
                    onChange={(e) => setServiceForm({ ...serviceForm, price: e.target.value.replace(/[^0-9.]/g, '') })}
                    placeholder="500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price Type</label>
                  <div className="flex rounded-lg overflow-hidden border border-gray-300">
                    <button
                      type="button"
                      onClick={() => setServiceForm({ ...serviceForm, priceType: 'fixed' })}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                        serviceForm.priceType === 'fixed'
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Fixed
                    </button>
                    <button
                      type="button"
                      onClick={() => setServiceForm({ ...serviceForm, priceType: 'onwards' })}
                      className={`flex-1 px-3 py-2 text-sm font-medium transition-colors ${
                        serviceForm.priceType === 'onwards'
                          ? 'bg-blue-500 text-white'
                          : 'bg-white text-gray-700 hover:bg-gray-50'
                      }`}
                    >
                      Onwards
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    {serviceForm.priceType === 'fixed' ? 'Shows as ₹500' : 'Shows as ₹500 onwards'}
                  </p>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows="3"
                  value={serviceForm.description}
                  onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                  placeholder="Describe the service..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Gender */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setServiceForm({ ...serviceForm, gender: serviceForm.gender === 'male' ? '' : 'male' })}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      serviceForm.gender === 'male'
                        ? 'bg-blue-500 text-white border-blue-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Male
                  </button>
                  <button
                    type="button"
                    onClick={() => setServiceForm({ ...serviceForm, gender: serviceForm.gender === 'female' ? '' : 'female' })}
                    className={`flex-1 px-4 py-2 rounded-lg border text-sm font-medium transition-colors ${
                      serviceForm.gender === 'female'
                        ? 'bg-pink-500 text-white border-pink-500'
                        : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    Female
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">Optional — leave unselected for all genders. Click again to deselect.</p>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowServiceModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingService}
                  className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingService ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Saving...</>
                  ) : (
                    <>{editingService ? 'Update Service' : 'Create Service'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== PACKAGE MODAL ===== */}
      {showPackageModal && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-5 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold">{editingPackage ? 'Edit Package' : 'Add Package'}</h3>
              <button onClick={() => setShowPackageModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSavePackage} className="p-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Package Name *</label>
                <input
                  type="text"
                  required
                  value={packageForm.name}
                  onChange={(e) => setPackageForm({ ...packageForm, name: e.target.value })}
                  placeholder="e.g., Complete Grooming Package"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows="2"
                  value={packageForm.description}
                  onChange={(e) => setPackageForm({ ...packageForm, description: e.target.value })}
                  placeholder="Describe the package..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Price & Duration */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) *</label>
                  <input
                    type="text"
                    required
                    value={packageForm.price}
                    onChange={(e) => setPackageForm({ ...packageForm, price: e.target.value })}
                    placeholder="1500"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Duration (mins)</label>
                  <input
                    type="text"
                    value={packageForm.duration}
                    onChange={(e) => setPackageForm({ ...packageForm, duration: e.target.value })}
                    placeholder="60"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Select Services */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Select Services *</label>
                {services.length > 0 ? (
                  <div className="border border-gray-300 rounded-lg max-h-48 overflow-y-auto">
                    {services.map(svc => (
                      <label
                        key={svc._id}
                        className={`flex items-center justify-between p-3 cursor-pointer hover:bg-blue-50 border-b border-gray-100 last:border-b-0 ${
                          packageForm.services.includes(svc._id) ? 'bg-blue-50' : ''
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={packageForm.services.includes(svc._id)}
                            onChange={() => togglePackageService(svc._id)}
                            className="w-4 h-4 text-blue-600"
                          />
                          <span className="text-sm font-medium">{svc.name}</span>
                        </div>
                        <span className="text-sm text-gray-500">₹{svc.price}</span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-gray-400 italic">No services available. Add services first.</p>
                )}
                {packageForm.services.length > 0 && (
                  <p className="text-xs text-blue-600 mt-1">{packageForm.services.length} service(s) selected</p>
                )}
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowPackageModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingPackage || packageForm.services.length === 0}
                  className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingPackage ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Saving...</>
                  ) : (
                    <>{editingPackage ? 'Update Package' : 'Create Package'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ===== ADDON MODAL ===== */}
      {showAddonModal && (
        <div className="fixed inset-0 bg-half-transparent flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[85vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b p-5 flex items-center justify-between z-10">
              <h3 className="text-xl font-bold">{editingAddon ? 'Edit Add-on' : 'Add Add-on'}</h3>
              <button onClick={() => setShowAddonModal(false)} className="p-2 hover:bg-gray-100 rounded-full">
                <FiX className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSaveAddon} className="p-5 space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Add-on Name *</label>
                <input
                  type="text"
                  required
                  value={addonForm.name}
                  onChange={(e) => setAddonForm({ ...addonForm, name: e.target.value })}
                  placeholder="e.g., Hair Serum"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Category & Price */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                  <select
                    value={addonForm.category}
                    onChange={(e) => setAddonForm({ ...addonForm, category: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    {addonCategories.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Price (₹) *</label>
                  <input
                    type="text"
                    inputMode="numeric"
                    required
                    value={addonForm.price}
                    onChange={(e) => setAddonForm({ ...addonForm, price: e.target.value.replace(/[^0-9.]/g, '') })}
                    placeholder="200"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  rows="2"
                  value={addonForm.description}
                  onChange={(e) => setAddonForm({ ...addonForm, description: e.target.value })}
                  placeholder="Describe the add-on..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Tags */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                <input
                  type="text"
                  value={addonForm.tags}
                  onChange={(e) => setAddonForm({ ...addonForm, tags: e.target.value })}
                  placeholder="premium, treatment, popular"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Display Order & Subsidy */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Display Order</label>
                  <input
                    type="number"
                    min="0"
                    value={addonForm.displayOrder}
                    onChange={(e) => setAddonForm({ ...addonForm, displayOrder: parseInt(e.target.value) || 0 })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="flex items-end pb-2">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={addonForm.eligibleForSubsidy}
                      onChange={(e) => setAddonForm({ ...addonForm, eligibleForSubsidy: e.target.checked })}
                      className="w-4 h-4 text-blue-600"
                    />
                    <span className="text-sm font-medium">Eligible for Subsidy</span>
                  </label>
                </div>
              </div>

              {/* Image */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Image</label>
                <div className="flex items-center gap-3">
                  {addonImageFile ? (
                    <img src={URL.createObjectURL(addonImageFile)} alt="Preview" className="w-16 h-16 object-cover rounded-lg" />
                  ) : editingAddon?.image ? (
                    <img src={editingAddon.image} alt="Current" className="w-16 h-16 object-cover rounded-lg" />
                  ) : null}
                  <label className="flex items-center gap-2 px-3 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg cursor-pointer text-sm">
                    <FiUpload /> {addonImageFile || editingAddon?.image ? 'Change' : 'Upload'}
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => setAddonImageFile(e.target.files[0])}
                      className="hidden"
                    />
                  </label>
                </div>
              </div>

              {/* Actions */}
              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => setShowAddonModal(false)}
                  className="flex-1 px-4 py-2.5 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={savingAddon}
                  className="flex-1 px-4 py-2.5 bg-blue-500 text-white rounded-lg hover:bg-blue-600 font-medium disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {savingAddon ? (
                    <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div> Saving...</>
                  ) : (
                    <>{editingAddon ? 'Update Add-on' : 'Create Add-on'}</>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default VendorServicesTab;
